import React from 'react';
import { Icon } from './Icons';

export default function DataExport({ mode, companyData }) {
  const [generating, setGenerating] = React.useState(false);
  const [done, setDone] = React.useState(false);

  function generatePDF() {
    setGenerating(true);

    const {
      blood = [], body = [], mental = [], fitness = [],
      totalEmployees = 0, companyName = 'Your Company'
    } = companyData || {};

    const avg = (arr, key) => {
      const vals = arr.map(d => parseFloat(d?.[key])).filter(v => !isNaN(v) && v > 0);
      return vals.length ? (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1) : 'N/A';
    };

    const highRisk = blood.filter(b =>
      parseFloat(b.fastingGlucose) >= 7 ||
      parseFloat(b.systolicBP) >= 140 ||
      parseFloat(b.totalCholesterol) >= 6
    ).length;
    const moderateRisk = blood.filter(b =>
      (parseFloat(b.fastingGlucose) >= 5.6 && parseFloat(b.fastingGlucose) < 7) ||
      (parseFloat(b.systolicBP) >= 120 && parseFloat(b.systolicBP) < 140)
    ).length;
    const lowRisk = Math.max(0, totalEmployees - highRisk - moderateRisk);
    const highStress = mental.filter(m => parseFloat(m.stressScore) >= 65).length;
    const burnoutRisk = mental.filter(m => parseFloat(m.burnoutScore) >= 60).length;
    const lowVitD = blood.filter(b => b.vitaminD && parseFloat(b.vitaminD) < 50).length;
    const engagement = totalEmployees > 0
      ? Math.round((blood.length + mental.length) / (totalEmployees * 2) * 100) : 0;

    const today = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

    const statusColor = (val, good, warn) => {
      if (val === 'N/A') return '#64748b';
      const v = parseFloat(val);
      if (v <= good) return '#059669';
      if (v <= warn) return '#d97706';
      return '#dc2626';
    };

    const riskColor = (count) => count > 0 ? '#dc2626' : '#059669';
    const riskBg = (count) => count > 0 ? '#fef2f2' : '#f0fdf4';

    const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>Levare Wellbeing Report — ${companyName}</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Helvetica Neue', Arial, sans-serif; background: #fff; color: #0f172a; font-size: 13px; line-height: 1.5; }
  .page { max-width: 800px; margin: 0 auto; padding: 40px; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 24px; border-bottom: 2px solid #0088cc; margin-bottom: 28px; }
  .brand { font-size: 22px; font-weight: 700; color: #0088cc; letter-spacing: -0.5px; }
  .brand span { color: #0f172a; }
  .report-meta { text-align: right; }
  .report-meta .title { font-size: 18px; font-weight: 600; color: #0f172a; }
  .report-meta .sub { font-size: 12px; color: #64748b; margin-top: 3px; }
  .section { margin-bottom: 28px; }
  .section-title { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: #64748b; margin-bottom: 14px; padding-bottom: 6px; border-bottom: 1px solid #e2e8f0; }
  .grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
  .grid-2 { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
  .grid-4 { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }
  .card { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px 16px; }
  .card.danger { background: #fef2f2; border-color: #fecaca; }
  .card.warning { background: #fffbeb; border-color: #fed7aa; }
  .card.success { background: #f0fdf4; border-color: #bbf7d0; }
  .metric-value { font-size: 26px; font-weight: 700; line-height: 1; margin-bottom: 3px; }
  .metric-label { font-size: 11px; color: #64748b; font-weight: 500; }
  .metric-unit { font-size: 11px; color: #94a3b8; }
  .flag-row { display: flex; justify-content: space-between; align-items: center; padding: 7px 0; border-bottom: 1px solid #f1f5f9; }
  .flag-label { font-size: 12px; color: #374151; }
  .flag-count { font-size: 14px; font-weight: 700; padding: 2px 10px; border-radius: 12px; }
  .bar-row { margin-bottom: 10px; }
  .bar-label { display: flex; justify-content: space-between; margin-bottom: 4px; font-size: 11px; color: #64748b; }
  .bar-track { height: 7px; background: #e2e8f0; border-radius: 4px; overflow: hidden; }
  .bar-fill { height: 100%; border-radius: 4px; }
  .recommendation { display: flex; gap: 12px; padding: 12px; background: #f0f9ff; border: 1px solid #bae6fd; border-radius: 8px; margin-bottom: 8px; }
  .rec-number { width: 22px; height: 22px; background: #0088cc; color: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 700; flex-shrink: 0; }
  .rec-text { font-size: 12px; color: #0f172a; line-height: 1.5; }
  .rec-text strong { color: #0088cc; }
  .footer { margin-top: 32px; padding-top: 16px; border-top: 1px solid #e2e8f0; display: flex; justify-content: space-between; font-size: 11px; color: #94a3b8; }
  .confidential { background: #fef3c7; border: 1px solid #fcd34d; border-radius: 6px; padding: 8px 14px; font-size: 11px; color: #92400e; margin-bottom: 20px; text-align: center; font-weight: 500; }
  @media print {
    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .page { padding: 20px; }
  }
</style>
</head>
<body>
<div class="page">

  <div class="header">
    <div>
      <div class="brand">levare<span>wellbeing</span></div>
      <div style="font-size:11px;color:#64748b;margin-top:4px;">Real Integrated Clinical Observations</div>
    </div>
    <div class="report-meta">
      <div class="title">Team Health Intelligence Report</div>
      <div class="sub">${companyName} &nbsp;·&nbsp; ${today}</div>
      <div class="sub" style="margin-top:2px;">Confidential — HR & Leadership only</div>
    </div>
  </div>

  <div class="confidential">
    CONFIDENTIAL — This report contains anonymised, aggregated health data. Individual employees cannot be identified. For HR & Leadership use only.
  </div>

  <!-- SNAPSHOT -->
  <div class="section">
    <div class="section-title">Workforce Health Snapshot</div>
    <div class="grid-4">
      <div class="card">
        <div class="metric-value" style="color:#0088cc">${totalEmployees}</div>
        <div class="metric-label">Total Employees</div>
      </div>
      <div class="card ${engagement >= 70 ? 'success' : engagement >= 40 ? 'warning' : 'danger'}">
        <div class="metric-value" style="color:${engagement >= 70 ? '#059669' : engagement >= 40 ? '#d97706' : '#dc2626'}">${engagement}%</div>
        <div class="metric-label">Data Coverage</div>
      </div>
      <div class="card ${highRisk > 0 ? 'danger' : 'success'}">
        <div class="metric-value" style="color:${highRisk > 0 ? '#dc2626' : '#059669'}">${highRisk}</div>
        <div class="metric-label">High Risk Employees</div>
      </div>
      <div class="card ${highStress > 0 ? 'warning' : 'success'}">
        <div class="metric-value" style="color:${highStress > 0 ? '#d97706' : '#059669'}">${highStress}</div>
        <div class="metric-label">High Stress Employees</div>
      </div>
    </div>
  </div>

  <!-- RISK BANDS -->
  <div class="section">
    <div class="section-title">Risk Stratification</div>
    <div class="grid-3">
      <div class="card danger">
        <div class="metric-value" style="color:#dc2626">${highRisk}</div>
        <div class="metric-label">High Risk</div>
        <div class="metric-unit" style="margin-top:4px;">Glucose ≥7, BP ≥140 or Chol ≥6</div>
      </div>
      <div class="card warning">
        <div class="metric-value" style="color:#d97706">${moderateRisk}</div>
        <div class="metric-label">Moderate Risk</div>
        <div class="metric-unit" style="margin-top:4px;">Pre-diabetic or elevated BP range</div>
      </div>
      <div class="card success">
        <div class="metric-value" style="color:#059669">${lowRisk}</div>
        <div class="metric-label">Low Risk</div>
        <div class="metric-unit" style="margin-top:4px;">No critical markers flagged</div>
      </div>
    </div>
  </div>

  <!-- AVERAGES -->
  <div class="section">
    <div class="section-title">Team Averages — Physical Health</div>
    <div class="grid-3">
      <div class="card">
        <div class="metric-value" style="color:${statusColor(avg(blood,'fastingGlucose'),5.5,7)}">${avg(blood,'fastingGlucose')}</div>
        <div class="metric-label">Avg Fasting Glucose</div>
        <div class="metric-unit">mmol/L · optimal &lt;5.5</div>
      </div>
      <div class="card">
        <div class="metric-value" style="color:${statusColor(avg(blood,'systolicBP'),120,140)}">${avg(blood,'systolicBP')}</div>
        <div class="metric-label">Avg Systolic BP</div>
        <div class="metric-unit">mmHg · optimal &lt;120</div>
      </div>
      <div class="card">
        <div class="metric-value" style="color:${statusColor(avg(blood,'totalCholesterol'),5,6)}">${avg(blood,'totalCholesterol')}</div>
        <div class="metric-label">Avg Cholesterol</div>
        <div class="metric-unit">mmol/L · optimal &lt;5.0</div>
      </div>
      <div class="card">
        <div class="metric-value" style="color:${parseFloat(avg(blood,'vitaminD')) >= 50 ? '#059669' : '#d97706'}">${avg(blood,'vitaminD')}</div>
        <div class="metric-label">Avg Vitamin D</div>
        <div class="metric-unit">nmol/L · optimal ≥50</div>
      </div>
      <div class="card">
        <div class="metric-value" style="color:${statusColor(avg(mental,'stressScore'),40,65)}">${avg(mental,'stressScore')}</div>
        <div class="metric-label">Avg Stress Score</div>
        <div class="metric-unit">/100 · optimal &lt;40</div>
      </div>
      <div class="card">
        <div class="metric-value" style="color:${parseFloat(avg(mental,'sleepHours')) >= 7 ? '#059669' : '#d97706'}">${avg(mental,'sleepHours')}</div>
        <div class="metric-label">Avg Sleep</div>
        <div class="metric-unit">hrs/night · optimal 7–9</div>
      </div>
    </div>
  </div>

  <!-- CLINICAL FLAGS -->
  <div class="section">
    <div class="section-title">Clinical Flags — Number of Employees Affected</div>
    <div class="grid-2">
      <div class="card">
        ${[
          ['Fasting Glucose ≥7 mmol/L (diabetic range)', blood.filter(b => parseFloat(b.fastingGlucose) >= 7).length, '#dc2626'],
          ['Fasting Glucose 5.6–6.9 mmol/L (pre-diabetic)', blood.filter(b => parseFloat(b.fastingGlucose) >= 5.6 && parseFloat(b.fastingGlucose) < 7).length, '#d97706'],
          ['Systolic BP ≥140 mmHg (hypertension)', blood.filter(b => parseFloat(b.systolicBP) >= 140).length, '#dc2626'],
          ['Systolic BP 120–139 mmHg (elevated)', blood.filter(b => parseFloat(b.systolicBP) >= 120 && parseFloat(b.systolicBP) < 140).length, '#d97706'],
        ].map(([label, count, color]) => `
          <div class="flag-row">
            <span class="flag-label">${label}</span>
            <span class="flag-count" style="background:${count > 0 ? color + '18' : '#f0fdf4'};color:${count > 0 ? color : '#059669'}">${count}</span>
          </div>
        `).join('')}
      </div>
      <div class="card">
        ${[
          ['Total Cholesterol ≥6 mmol/L', blood.filter(b => parseFloat(b.totalCholesterol) >= 6).length, '#dc2626'],
          ['Vitamin D &lt;50 nmol/L (deficient)', blood.filter(b => b.vitaminD && parseFloat(b.vitaminD) < 50).length, '#d97706'],
          ['Stress Score ≥65/100 (high)', mental.filter(m => parseFloat(m.stressScore) >= 65).length, '#dc2626'],
          ['Burnout Score ≥60/100 (at risk)', mental.filter(m => parseFloat(m.burnoutScore) >= 60).length, '#d97706'],
        ].map(([label, count, color]) => `
          <div class="flag-row">
            <span class="flag-label">${label}</span>
            <span class="flag-count" style="background:${count > 0 ? color + '18' : '#f0fdf4'};color:${count > 0 ? color : '#059669'}">${count}</span>
          </div>
        `).join('')}
      </div>
    </div>
  </div>

  <!-- DATA COVERAGE -->
  <div class="section">
    <div class="section-title">Data Coverage</div>
    <div class="grid-2">
      ${[
        ['Blood results uploaded', blood.length, totalEmployees, '#0088cc'],
        ['Mental health data', mental.length, totalEmployees, '#059669'],
        ['Body metrics', body.length, totalEmployees, '#d97706'],
        ['Fitness data', fitness.length, totalEmployees, '#7c3aed'],
      ].map(([label, count, total, color]) => `
        <div class="bar-row">
          <div class="bar-label"><span>${label}</span><span>${count} / ${total} (${total > 0 ? Math.round(count/total*100) : 0}%)</span></div>
          <div class="bar-track"><div class="bar-fill" style="width:${total > 0 ? Math.round(count/total*100) : 0}%;background:${color}"></div></div>
        </div>
      `).join('')}
    </div>
  </div>

  <!-- RECOMMENDATIONS -->
  <div class="section">
    <div class="section-title">Levare Recommendations</div>
    ${highRisk > 0 ? `<div class="recommendation"><div class="rec-number">1</div><div class="rec-text"><strong>Targeted clinical intervention:</strong> ${highRisk} employee${highRisk > 1 ? 's are' : ' is'} in the high-risk category. A confidential one-to-one health consultation with a Levare clinician is recommended to assess diabetes and cardiovascular risk.</div></div>` : ''}
    ${highStress > 0 ? `<div class="recommendation"><div class="rec-number">${highRisk > 0 ? 2 : 1}</div><div class="rec-text"><strong>Stress management programme:</strong> ${highStress} employee${highStress > 1 ? 's have' : ' has'} a stress score ≥65. Chronic stress directly worsens glucose, cholesterol and blood pressure. A Levare stress management workshop addresses all three simultaneously.</div></div>` : ''}
    ${lowVitD > 0 ? `<div class="recommendation"><div class="rec-number">${[highRisk > 0, highStress > 0].filter(Boolean).length + 1}</div><div class="rec-text"><strong>Vitamin D supplementation:</strong> ${lowVitD} employee${lowVitD > 1 ? 's are' : ' is'} deficient in Vitamin D (&lt;50 nmol/L). This is one of the most common and easiest-to-correct deficiencies in UK workforces. Consider a group supplement order or education session.</div></div>` : ''}
    ${engagement < 60 ? `<div class="recommendation"><div class="rec-number">${[highRisk > 0, highStress > 0, lowVitD > 0].filter(Boolean).length + 1}</div><div class="rec-text"><strong>Increase data coverage:</strong> Only ${engagement}% of employees have uploaded health data. Without this, risk stratification is incomplete. We recommend a manager-led engagement campaign — employees with full data get 3x more value from the platform.</div></div>` : ''}
    ${highRisk === 0 && highStress === 0 && lowVitD === 0 && engagement >= 60 ? `<div class="recommendation"><div class="rec-number">1</div><div class="rec-text"><strong>Maintain and monitor:</strong> No urgent clinical flags detected. Your team is performing well. Focus on maintaining engagement and consider preventative programmes such as nutrition workshops or fitness challenges to sustain performance.</div></div>` : ''}
  </div>

  <div class="footer">
    <span>Levare Wellbeing Platform &nbsp;·&nbsp; levarewellbeing.com &nbsp;·&nbsp; contact@levarewellbeing.com</span>
    <span>Generated ${today} &nbsp;·&nbsp; Confidential</span>
  </div>

</div>
</body>
</html>`;

    // Open in new tab and trigger print dialog
    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    setTimeout(() => {
      win.print();
      setGenerating(false);
      setDone(true);
      setTimeout(() => setDone(false), 3000);
    }, 800);
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem', padding: '1rem 1.25rem', background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', flexWrap: 'wrap' }}>
      <Icon name="file" size={16} color="var(--text3)" />
      <div style={{ flex: 1, minWidth: 200 }}>
        <div style={{ fontSize: '.82rem', fontWeight: 500, color: 'var(--text)', marginBottom: '.1rem' }}>Export Health Intelligence Report</div>
        <div style={{ fontSize: '.72rem', color: 'var(--text3)', fontWeight: 300 }}>Anonymised PDF report — risk stratification, team averages, clinical flags and recommendations</div>
      </div>
      <button onClick={generatePDF} disabled={generating || !companyData} style={{ padding: '.6rem 1.25rem', borderRadius: 'var(--radius)', border: 'none', background: done ? 'var(--success)' : 'var(--primary)', color: '#080f18', fontFamily: 'var(--font-body)', fontSize: '.78rem', fontWeight: 500, cursor: generating || !companyData ? 'not-allowed' : 'pointer', opacity: generating || !companyData ? .6 : 1, display: 'flex', alignItems: 'center', gap: '.4rem', whiteSpace: 'nowrap', flexShrink: 0 }}>
        <Icon name="download" size={14} color="#080f18" />
        {done ? 'Report Ready' : generating ? 'Generating…' : 'Download Report'}
      </button>
    </div>
  );
}
